import { toggle } from "shared/utils/objectUtils";

// How to add new roles:
// 1. Add the new role to the role config in drops-backend.
// See roles-config.yml in the backend repo on how roles are assigned to user.
// 2. Add the new role to the UserRole enum and ROLE_TEXTS below.
// 3. Make sure the UserRole value matches exactly with the role config in the backend.
// 4. Deploy both backend and frontend with the updated role config.

export enum UserRole {
  personellstyrerkond = "personellstyrerkond",
  personellstyrerlok = "personellstyrerlok",
  personellstyrervest = "personellstyrervest",
  personellstyrerekstra = "personellstyrerekstra",
  vaktleder = "vaktleder",
  materiellstyrerlokal = "materiellstyrerlokal",
  materiellstyrerregion = "materiellstyrerregion",
  trafikkplanlegger = "trafikkplanlegger", // TODO: Fjern når alle er over på 60/63
  trafikkplanlegger60 = "trafikkplanlegger60",
  trafikkplanlegger63 = "trafikkplanlegger63",
  lokleder1 = "lokleder1",
  lokleder2 = "lokleder2",
  transportleder = "transportleder",
  driftledningen = "driftledningen",
  oks = "oks", // Operativ Koordinator Sporbrudd
  okm = "okm", // Operativ Koordinator Materiell
}

// Utility to check whether the currently selected role has access to the
// MeasureOverview tab, an overview of alternative transport measures
export const ROLES_WITH_ACCESS_TO_MEASURE_OVERVIEW = new Set([
  UserRole.vaktleder,
  UserRole.transportleder,
  UserRole.trafikkplanlegger,
  UserRole.trafikkplanlegger60,
  UserRole.trafikkplanlegger63,
  UserRole.oks,
]);

export const isValidUserRole = (value: string | undefined): value is UserRole =>
  !!value && Object.keys(UserRole).includes(value);

const BASE_ROLES = [
  {
    text: "Vaktleder",
    value: UserRole.vaktleder,
    abbreviation: "vl",
  },
  {
    text: "Transportleder",
    value: UserRole.transportleder,
    abbreviation: "tl",
  },
  {
    text: "Personellstyrer (konduktør)",
    value: UserRole.personellstyrerkond,
    abbreviation: "psk",
  },
  {
    text: "Personellstyrer (lokomotivfører)",
    value: UserRole.personellstyrerlok,
    abbreviation: "psl",
  },
  {
    text: "Personellstyrer (vest)",
    value: UserRole.personellstyrervest,
    abbreviation: "psv",
  },
  {
    text: "Personellstyrer (ekstra)",
    value: UserRole.personellstyrerekstra,
    abbreviation: "pse",
  },
  {
    text: "Materiellstyrer (lokaltog)",
    value: UserRole.materiellstyrerlokal,
    abbreviation: "msl",
  },
  {
    text: "Materiellstyrer (regiontog)",
    value: UserRole.materiellstyrerregion,
    abbreviation: "msr",
  },
  {
    text: "Operativ Koordinator Materiell",
    value: UserRole.okm,
    abbreviation: "okm",
  },
];

const SWEDISH_ROLE = {
  text: "Driftledningen",
  value: UserRole.driftledningen,
  abbreviation: "dl",
};

const OKS_ROLE = {
  text: "Operativ Koordinator Sporbrudd",
  value: UserRole.oks,
  abbreviation: "oks",
};

export const ROLE_TEXTS = [
  ...BASE_ROLES,
  {
    text: "Lokleder 1",
    value: UserRole.lokleder1,
    abbreviation: "ll1",
  },
  {
    text: "Lokleder 2",
    value: UserRole.lokleder2,
    abbreviation: "ll2",
  },
  {
    text: "Trafikkplanlegger (60)",
    value: UserRole.trafikkplanlegger60,
    abbreviation: "tp60",
  },
  {
    text: "Trafikkplanlegger (63)",
    value: UserRole.trafikkplanlegger63,
    abbreviation: "tp63",
  },
  SWEDISH_ROLE,
  OKS_ROLE,
];

export const TODO_ROLES = [
  ...BASE_ROLES,
  {
    text: "Lokleder",
    value: UserRole.lokleder1,
    abbreviation: "ll",
  },
  {
    text: "Trafikkplanlegger",
    value: UserRole.trafikkplanlegger60,
    abbreviation: "tp",
  },
  OKS_ROLE,
];

export const getTodoRoleText = (roleValue: UserRole) => {
  if (roleValue === UserRole.lokleder1) {
    return "Lokleder";
  }

  if (roleValue === UserRole.trafikkplanlegger60) {
    return "Trafikkplanlegger";
  }

  return ROLE_TEXTS.find((role) => role.value === roleValue)?.text;
};

export const getTodoRole = (currentRole: UserRole) => {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (currentRole) {
    case UserRole.lokleder1:
    case UserRole.lokleder2:
      return [UserRole.lokleder1];

    case UserRole.trafikkplanlegger60:
    case UserRole.trafikkplanlegger63:
      return [UserRole.trafikkplanlegger60];

    default:
      return [currentRole];
  }
};

const LOKLEDER_ROLES = [UserRole.lokleder1, UserRole.lokleder2];

export const getUpdatedRoles = (selectedRoles: UserRole[], role: UserRole) => {
  if (role !== UserRole.lokleder1) {
    return toggle(selectedRoles, role);
  }

  const hasBoth = LOKLEDER_ROLES.every((r) => selectedRoles.includes(r));

  return hasBoth
    ? selectedRoles.filter((r) => !LOKLEDER_ROLES.includes(r))
    : [...new Set([...selectedRoles, ...LOKLEDER_ROLES])];
};

export const getSelectedTodoRoleCount = (selectedRoles: string[]) =>
  TODO_ROLES.filter((role) => selectedRoles.includes(role.value)).length;
